import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { IOID4VCIClientCreateOfferUriResponse } from "@sphereon/ssi-sdk.oid4vci-issuer-rest-client";
import { QRRenderingProps, QRType } from "@sphereon/ssi-sdk.qr-code-generator";
import { IssueStatus, IssueStatusResponse } from "@sphereon/oid4vci-common";
import agent from "../../../../../agent";
const short = require("short-uuid");

interface TravelerProfileIssuerProps {
  fullName: string;
}

type CredentialType = {
  id: string;
  displayName: string;
  data: any;
};

const TravelerProfileIssuer = (props: TravelerProfileIssuerProps) => {
  const [currentCredentialIndex, setCurrentCredentialIndex] = useState(0);
  const [qrData, setQrData] = useState<any>();
  const [uri, setUri] = useState("");
  const [preAuthCode, setPreAuthCode] = useState("");
  const [qrCode, setQrCode] = useState<JSX.Element>();
  const [credentialsIssued, setCredentialsIssued] = useState<string[]>([]);

  const credentialsToIssue: CredentialType[] = [
    {
      id: "CommonTravelPreferences",
      displayName: "Common Travel Preferences",
      data: {
        gender: "Female",
        birthdate: "1985-04-23",
        preferredLanguage: "en-US",
        nationality: "CH",
        accessibilityNeeds: "Wheelchair access required",
        dietaryRestrictions: "vegetarian, gluten-free",
        smokingPreference: "non-smoking",
        petAllergies: "cats",
        rewardsPrograms: "Frequent Traveler Plus",
        email: "bogdan.mocanu@netcetera.com",
        telephone: "+41 555 01 23",
      }
    },
    {
      id: "PassportAndVisaInformation",
      displayName: "Passport & Visa Information",
      data: {
        documentTypes: "passport, visa",
        issuingCountry: "CH",
        expirationDate: "2030-12-31",
        documentNumber: "C12345678",
        nationality: "CH",
        fullName: "Jane Doe",
        birthPlace: "Zurich, CH"
      }
    },
    {
      id: "MedicalInformation",
      displayName: "Medical Information",
      data: {
        bloodType: "A+",
        allergies: "peanuts, shellfish",
        medications: "insulin",
        medicalConditions: "Type 1 Diabetes",
        emergencyContactName: "John Doe",
        emergencyContactPhone: "+41 555 01 56",
        insuranceProvider: "Global Health Inc.",
        insurancePolicyNumber: "GHI-789123"
      }
    },
    {
      id: "ActivityPreferences",
      displayName: "Activity Preferences",
      data: {
        preferredActivities: "hiking, museums",
        fitnessLevel: "moderate",
        interests: "history, architecture",
        equipmentNeeds: "hiking boots",
        skillLevels: "skiing: intermediate, swimming: advanced"
      }
    },
    {
      id: "AirPreferences",
      displayName: "Air Travel Preferences",
      data: {
        preferredAirlines: "Delta, Emirates",
        seatPreferences: "window",
        frequentFlyerPrograms: "SkyMiles, Skywards",
        knownTravelerNumber: "23423423423423",
        preferredMealType: "vegetarian",
        specialAssistanceRequired: "true"
      }
    },
    {
      id: "LodgingPreferences",
      displayName: "Lodging Preferences",
      data: {
        preferredHotelChains: "Marriott, Hilton",
        roomType: "suite",
        bedType: "king",
        amenities: "pool, gym, spa",
        smokingPreference: "non-smoking"
      }
    },
    {
      id: "RentalCarPreferences",
      displayName: "Rental Car Preferences",
      data: {
        preferredCarRentalCompanies: "Hertz, Avis",
        carType: "SUV",
        insuranceCoverage: "full",
        fuelPreference: "electric",
        loyaltyPrograms: "driving expert group",
        specialEquipment: "child seat"
      }
    },
    {
      id: "RestaurantPreferences",
      displayName: "Restaurant Preferences",
      data: {
        preferredCuisines: "Italian, Japanese",
        dietaryRestrictions: "vegetarian",
        priceRange: "$$$",
        atmosphere: "casual, family-friendly",
        favoriteDishes: "pasta, sushi"
      }
    },
    {
      id: "StaticVenuesPreferences",
      displayName: "Venue Preferences",
      data: {
        venueTypes: "museums, theaters",
        accessibilityNeeds: "wheelchair access",
        atmosphere: "casual, family-friendly",
        interests: "art, history"
      }
    },
    {
      id: "ThingsToDoSpa",
      displayName: "Spa Preferences",
      data: {
        treatmentPreferences: "massage, facial",
        therapistGenderPreference: "female",
        preferredSpaTypes: "relaxation, detox",
        atmosphere: "zen, tranquil",
        budget: "$$$"
      }
    },
    {
      id: "ThingsToDoSightseeingTour",
      displayName: "Sightseeing Preferences",
      data: {
        tourTypes: "historical, architectural",
        language: "English",
        groupSize: "small",
        themes: "History, Culture",
        duration: "2h"
      }
    },
    {
      id: "ThingsToDoSkiing",
      displayName: "Skiing Preferences",
      data: {
        skillLevel: "intermediate",
        resortPreferences: "Alps, Rockies",
        equipmentRental: "true",
        lessonInterest: "false",
        preferredTerrain: "powder",
        apresSkiActivities: "dining, spa"
      }
    }
  ];

  const renderingProps: QRRenderingProps = {
    bgColor: "#FBFBFB",
    fgColor: "black",
    level: "L",
    size: 330,
  };

  useEffect(() => {
    if (currentCredentialIndex < credentialsToIssue.length) {
      generateQRForCurrentCredential();
    }
  }, [currentCredentialIndex]);

  const generateQRForCurrentCredential = async () => {
    const shortUuid = short.generate();
    const currentCredential = credentialsToIssue[currentCredentialIndex];

    const uriData: IOID4VCIClientCreateOfferUriResponse = 
      await agent.oid4vciClientCreateOfferUri({
        grants: {
          "urn:ietf:params:oauth:grant-type:pre-authorized_code": {
            "pre-authorized_code": shortUuid,
            user_pin_required: false,
          },
        },
        credentialDataSupplierInput: currentCredential.data,
        credentials: [currentCredential.id],
      });

    const dataQR: any = {
      object: uriData.uri,
      type: QRType.URI,
      id: "567",
    };

    setUri(uriData.uri);
    setPreAuthCode(shortUuid);
    setQrData(dataQR);

    agent
      .qrURIElement({
        data: dataQR,
        renderingProps: renderingProps,
      })
      .then(setQrCode);

    const intervalId = setInterval(() => {
      agent
        .oid4vciClientGetIssueStatus({ id: shortUuid })
        .then((status: IssueStatusResponse) => {
          if (status.status === IssueStatus.CREDENTIAL_ISSUED) {
            clearInterval(intervalId);
            setQrCode(undefined);
            setCredentialsIssued(prev => [...prev, currentCredential.displayName]);
            setCurrentCredentialIndex(prev => prev + 1);
          } else if (status.status === IssueStatus.ERROR) {
            console.error(status.error);
            clearInterval(intervalId);
          }
        })
        .catch(error => {
          clearInterval(intervalId);
          console.error(`ERROR: ${error.message}`);
        });
    }, 1000);
  };

  return (
    <Container >
      {currentCredentialIndex < credentialsToIssue.length ? (
        <>
        <Row className="my-5" style={{ height: "100%" }}>
            <Col md={12}>
            <h3>Issuing "{credentialsToIssue[currentCredentialIndex].displayName}"</h3>
            </Col> 
        </Row>
        <Row className="my-5" style={{ height: "100%" }}>
              <Col md={6} className="d-flex align-items-left">
                
                <div className="align-items-left">
                    <ul>
                    {Object.entries(credentialsToIssue[currentCredentialIndex].data).map(([key, value]) => (
                        <li key={key}>
                        <strong>{key}:</strong> {JSON.stringify(value)}
                        </li>
                    ))}
                    </ul>
                </div>
              </Col>
              <Col
                md={6}
                className="text-center d-flex flex-column justify-content-center align-items-center"
              >
                <div>
                <div className={"custom" + currentCredentialIndex}>{qrCode}</div>
                </div>
              </Col>
            </Row>
            </>
      ) : (
        <div className="success-message justify-content-center align-items-center text-center d-flex flex-column">
          <h2>All Travel Credentials Issued!</h2>
          <h4>You received:</h4>
          <ul>
            {credentialsIssued.map((cred, index) => (
              <li key={index}>{cred}</li>
            ))}
          </ul>
          <h5>Your digital travel identity is now complete!</h5>
        </div>
      )}
    </Container>
  );
};

export default TravelerProfileIssuer;
